import { useState, useEffect, useCallback } from 'react'
import { db } from '../../../firebase'
import {
  collection,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { useToast } from '../../../lib/ToastContext'

function toMillis(value) {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.toDate === 'function') return value.toDate().getTime()
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase()
}

function isPendingStatus(value) {
  const normalized = normalizeStatus(value)
  return normalized === 'pending_photos' || normalized === 'pending'
}

function sortByCreatedAtDesc(items = []) {
  return [...items].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt))
}

export function usePendingProducts() {
  const [pendingProducts, setPendingProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const toast = useToast()

  useEffect(() => {
    let fallbackUnsubscribe = null

    const startFallbackListener = () => {
      if (fallbackUnsubscribe) return
      const fallbackQuery = query(
        collection(db, 'products'),
        orderBy('createdAt', 'desc'),
        limit(200)
      )
      fallbackUnsubscribe = onSnapshot(
        fallbackQuery,
        (snapshot) => {
          const prods = snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => isPendingStatus(p?.status))
          setPendingProducts(sortByCreatedAtDesc(prods).slice(0, 50))
          setLoading(false)
        },
        (fallbackErr) => {
          console.error('Fallback pending products query failed:', fallbackErr)
          toast.error('Failed to load pending products')
          setLoading(false)
        }
      )
    }

    const primaryQuery = query(
      collection(db, 'products'),
      where('status', '==', 'pending_photos'),
      orderBy('createdAt', 'desc'),
      limit(50)
    )

    const unsubscribe = onSnapshot(
      primaryQuery,
      (snapshot) => {
        const prods = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }))
        setPendingProducts(sortByCreatedAtDesc(prods))
        setLoading(false)
      },
      (err) => {
        console.error('Failed to load pending products:', err)
        // Missing index or query issues should not block office flow.
        // Fallback listener loads latest products and filters pending locally.
        startFallbackListener()
      }
    )

    return () => {
      unsubscribe()
      if (fallbackUnsubscribe) fallbackUnsubscribe()
    }
  }, [])

  const markActive = useCallback(async (productId) => {
    try {
      await updateDoc(doc(db, 'products', productId), {
        status: 'active',
        updatedAt: serverTimestamp()
      })
      toast.success('Product marked as active')
    } catch (err) {
      console.error('Failed to mark active:', err)
      toast.error('Failed to mark as active')
      throw err
    }
  }, [])

  return {
    pendingProducts,
    loading,
    markActive
  }
}

export default usePendingProducts
